/**
 * The client's mirror of a running round and the pure maths behind low-latency rendering:
 * applying tick frames, predicting the own cycle from its queued turns, and reconciling that
 * prediction when the server's frame arrives. No DOM, no store — unit-tested on its own.
 */
import {
  type CycleSnapshot,
  type CyclesRoundView,
  type CyclesTickFrame,
  cellIndex,
  cellsFromCycles,
  DX,
  DY,
  extendPath,
  type Heading,
  inBounds,
  popApplicable,
  turnLeft,
  turnRight,
} from '@xox/shared'
import { MAX_PENDING_TICKS } from './anim'

/** A cycle plus where its head was one tick ago, for interpolation. */
interface LiveCycle extends CycleSnapshot {
  px: number
  py: number
}

export interface LiveRound {
  no: number
  cycleIds: string[]
  /** Server time of tick 0, refined from the frames' scheduled times. */
  anchor: number
  tick: number
  cycles: LiveCycle[]
  cells: Uint8Array
  /** The last frame did not follow the previous one: ask for a snapshot. */
  gap: boolean
  /** Ids that crashed in the last applied frame. */
  deaths: string[]
}

/** A turn the player queued locally, stamped with the tick it was queued on. */
export interface PendingTurn {
  h: Heading
  tick: number
}

export function liveFromRound(round: CyclesRoundView): LiveRound {
  return {
    no: round.no,
    cycleIds: round.cycleIds,
    anchor: round.startAt,
    tick: round.tick,
    cycles: round.cycles.map((c) => ({ ...c, path: [...c.path], px: c.x, py: c.y })),
    cells: cellsFromCycles(round.cycles),
    gap: false,
    deaths: [],
  }
}

/** Applies one server frame; frames for another round or from the past are ignored. */
export function applyTick(live: LiveRound, frame: CyclesTickFrame, tickMs: number): LiveRound {
  if (frame.r !== live.no || frame.t <= live.tick) return live
  const gap = frame.t !== live.tick + 1
  const cells = new Uint8Array(live.cells)
  const deaths: string[] = []
  const cycles = live.cycles.map((c, i) => {
    const x = frame.c[i * 3]
    const y = frame.c[i * 3 + 1]
    const h = frame.c[i * 3 + 2]
    if (x === undefined || y === undefined || h === undefined) return c
    if (h < 0) {
      if (!c.alive) return c
      deaths.push(c.id)
      return { ...c, alive: false, diedAt: frame.t, px: c.x, py: c.y, x, y }
    }
    const heading = h as Heading
    if (x === c.x && y === c.y) return { ...c, heading, px: c.x, py: c.y }
    // Paint every cell between the old head and the new one (one cell, more after a gap).
    let cx = c.x
    let cy = c.y
    const sx = Math.sign(x - cx)
    const sy = Math.sign(y - cy)
    while (cx !== x || cy !== y) {
      cx += sx
      cy += sy
      cells[cellIndex(cx, cy)] = i + 1
    }
    return { ...c, px: c.x, py: c.y, x, y, heading, path: extendPath(c.path, x, y) }
  })
  const sample = frame.at - frame.t * tickMs
  const anchor = live.anchor === 0 ? sample : live.anchor * 0.8 + sample * 0.2
  return { ...live, tick: frame.t, cycles, cells, gap, deaths, anchor }
}

/** The heading a cycle will have once every queued turn has applied. */
export function predictedHeading(heading: Heading, pending: readonly Heading[]): Heading {
  let h = heading
  let queue: Heading[] = [...pending]
  while (queue.length > 0) {
    const r = popApplicable(queue, h)
    if (r.turn === null) break
    h = r.turn
    queue = r.queue
  }
  return h
}

/** Tap left / tap right, relative to where the cycle is (or is about to be) heading. */
export function relativeTurn(side: 'left' | 'right', heading: Heading): Heading {
  return side === 'left' ? turnLeft(heading) : turnRight(heading)
}

export interface PredictedStep {
  x: number
  y: number
  heading: Heading
  /** The step runs into something: the cycle will crash there. */
  blocked: boolean
}

/** Where the cycle will be after 1..n ticks if the server applies the queue the way we did. */
export function predictSteps(
  c: CycleSnapshot,
  pending: readonly Heading[],
  n: number,
  cells: Uint8Array,
): PredictedStep[] {
  let { x, y, heading: h } = c
  let queue: Heading[] = [...pending]
  const out: PredictedStep[] = []
  for (let i = 0; i < n; i++) {
    const r = popApplicable(queue, h)
    queue = r.queue
    if (r.turn !== null) h = r.turn
    const nx = x + DX[h]!
    const ny = y + DY[h]!
    const blocked = !inBounds(nx, ny) || cells[cellIndex(nx, ny)] !== 0
    out.push({ x: nx, y: ny, heading: h, blocked })
    if (blocked) break
    x = nx
    y = ny
  }
  return out
}

/**
 * The own queue after a confirmed frame: the turn the server just applied (and anything queued
 * before it) is dropped, turns the server never picked up expire, a dead cycle keeps nothing.
 */
export function reconcilePending(
  pending: readonly PendingTurn[],
  prevHeading: Heading,
  confirmed: CycleSnapshot,
  tick: number,
): PendingTurn[] {
  if (!confirmed.alive) return []
  let queue = [...pending]
  if (confirmed.heading !== prevHeading) {
    const i = queue.findIndex((p) => p.h === confirmed.heading)
    queue = i >= 0 ? queue.slice(i + 1) : []
  }
  return queue.filter((p) => tick - p.tick <= MAX_PENDING_TICKS && p.h !== confirmed.heading)
}
